import {useCallback} from 'react'
import useIsomorphicLayoutEffect from './use-isomorphic-layout-effect'

/**
 * Writes one CSS custom property inline on one element, and removes it again.
 *
 * Inline (`element.style.setProperty`) rather than a stylesheet rule: it beats
 * the recipe's class-rule default, it inherits to descendants, and it is cheap
 * enough to update every animation frame.
 *
 * `publish(value)` writes on the target. No value, or no target, writes nothing.
 * The property is removed when the target or `varName` changes and on unmount,
 * always from the element it was written on. Nothing is re-written after such a
 * change, so pass a target that stays the same for the life of the hook.
 *
 * One caller owns a given name on a given target.
 *
 * @param {string} varName - The custom property to write, e.g. `'--panel-size'`.
 * @param {Object} [options] - Optional configuration.
 * @param {HTMLElement} [options.target] - Element to write on.
 * @returns {function(string=): void} `publish(value)`.
 *
 * @example
 * const publish = useInlineCssVar('--bar-viewport-offset', {
 *   target: isServer ? null : document.documentElement
 * })
 *
 * publish('64px')
 */
const useInlineCssVar = (varName, {target} = {}) => {
    const publish = useCallback(
        (value) => {
            if (!target || value === null || value === undefined) return
            target.style.setProperty(varName, value)
        },
        [target, varName]
    )

    // The property belongs to the element it was written on, so the cleanup
    // closes over that element rather than reading the current target.
    useIsomorphicLayoutEffect(() => {
        if (!target) return undefined
        return () => target.style.removeProperty(varName)
    }, [target, varName])

    return publish
}

export default useInlineCssVar
